import React, { useRef } from 'react';

import SurfaceTreatmentComponent from './SurfaceTreatment';
import ColorList from './ColorList';
import DimensionList from './DimensionList';
import { useAssignment } from '../hooks/useAssignment';
import { useMaterialInfoService } from '../hooks/useMaterialInfoService';
import QueryParams from '../utils/QueryParams';
import { MaterialColor, MaterialDimension } from '../types/CommonTypes';
import { ConditionType } from '../hooks/usePartNrAssignment';

interface Props {
    activeTab: string;
    onTabChange: (tab: string) => void;
    iFrameUrl: string;
}

export default function MaterialTabs({ activeTab, onTabChange, iFrameUrl }: Props): JSX.Element {
    const devMode = QueryParams.devMode;
    const isCreo = QueryParams.useCreoConnector;
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const { selectTreatment, assignCondition, assignConditionToAlternate, selectedAlternateId } = useAssignment();
    const { validSTs, allSTs, colors, dimensions, isAlternateLoading } = useMaterialInfoService();

    const handleConditionSelect = (condition: MaterialColor | MaterialDimension, type: ConditionType) => {
        if (selectedAlternateId) {
            assignConditionToAlternate(selectedAlternateId, condition, type);
        } else {
            assignCondition(condition, type);
        }
    };

    return (
        <>
            <div className="tabbar">
                <div className="tabrow">
                    <button
                        onClick={() => onTabChange('iframe')}
                        className={`tab ${activeTab === 'iframe' ? 'active' : ''}`}
                        aria-selected={activeTab === 'iframe'}
                    >
                        Base Material
                    </button>
                    <button
                        onClick={() => onTabChange('grid')}
                        className={`tab ${activeTab === 'grid' ? 'active' : ''}`}
                        aria-selected={activeTab === 'grid'}
                        disabled={!!selectedAlternateId}
                        title={selectedAlternateId ? 'Surface treatment is not applicable for alternate materials' : undefined}
                    >
                        SurfaceTreatment
                    </button>
                    {(!isCreo || !!selectedAlternateId) && colors.length > 0 && (
                        <button
                            onClick={() => onTabChange('colors')}
                            className={`tab ${activeTab === 'colors' ? 'active' : ''}`}
                            aria-selected={activeTab === 'colors'}
                        >
                            Colors
                        </button>
                    )}
                    {(!isCreo || !!selectedAlternateId) && dimensions.length > 0 && (
                        <button
                            onClick={() => onTabChange('dimensions')}
                            className={`tab ${activeTab === 'dimensions' ? 'active' : ''}`}
                            aria-selected={activeTab === 'dimensions'}
                        >
                            Dimensions
                        </button>
                    )}
                </div>
            </div>

            <div className="contentWindow">
                <iframe
                    ref={iframeRef}
                    src={iFrameUrl}
                    title="Total Materia Portal"
                    className="prostep-iframe"
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                    allow="cross-origin-isolated"
                    style={{ display: activeTab === 'iframe' ? 'block' : 'none' }}
                />
                <div style={{ width: '100%', height: '100%', display: activeTab === 'grid' ? 'block' : 'none' }}>
                    <SurfaceTreatmentComponent
                        devMode={devMode}
                        setSelectedTreatment={selectTreatment}
                        treatments={allSTs}
                        validSTs={validSTs}
                    />
                </div>
                {colors.length > 0 && (
                    <div style={{ width: '100%', height: '100%', display: activeTab === 'colors' ? 'block' : 'none' }}>
                        <ColorList
                            colors={colors}
                            onSelect={color => handleConditionSelect(color, 'color')}
                            loading={isAlternateLoading}
                        />
                    </div>
                )}
                {dimensions.length > 0 && (
                    <div style={{ width: '100%', height: '100%', display: activeTab === 'dimensions' ? 'block' : 'none' }}>
                        <DimensionList
                            dimensions={dimensions}
                            onSelect={dimension => handleConditionSelect(dimension, 'dimension')}
                            loading={isAlternateLoading}
                        />
                    </div>
                )}
            </div>
        </>
    );
}
